import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, FileDown, ArrowLeft, ArrowUp, ArrowDown } from "lucide-react";
import { formatNum } from "@/lib/format";
import { createPdfDoc, withDefaults, drawTotalsBlock, finalizePdf } from "@/lib/pdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { enumerateDays, localDateFromISO } from "@/lib/dates";

/**
 * ClientConsumption — shared component used on /client (as three tiles) AND on /client/reports (as a section/tab).
 * The parent passes in the currently-active date range + store filter. All fetches, drill-downs and exports
 * respect that filter. Quantity-only, cost-blind by design.
 *
 * Props:
 *   from, to         — ISO YYYY-MM-DD (inclusive)
 *   storeId          — "" for all stores
 *   variant          — "tiles" (client dashboard) | "section" (reports page)
 */
export function ClientConsumption({ from, to, storeId, variant = "tiles" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);      // "BIB" | "CO2" | "Water Jar" — modal open state
  const [selectedDay, setSelectedDay] = useState(null); // ISO date for day drill-down

  // Fetch consumption whenever inputs change
  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: from, to_date: to };
      if (storeId) params.store_id = storeId;
      const { data: d } = await api.get("/client/consumption", { params });
      setData(d);
    } catch { toast.error("Failed to load consumption"); }
    finally { setLoading(false); }
  }, [from, to, storeId]);

  useEffect(() => { refetch(); }, [refetch]);

  const rangeLabel = `${from} to ${to}`;
  const items = [
    { code: "BIB",       label: "BIB consumed",       unit: "units", color: "text-brand-red"  },
    { code: "CO2",       label: "CO2 consumed",       unit: "units", color: "text-blue-700"   },
    { code: "Water Jar", label: "Water Jar consumed", unit: "jars",  color: "text-cyan-700"   },
  ];

  // Day list in the window — pure YYYY-MM-DD strings, no timezone conversion (avoids the -1 day IST bug).
  const days = useMemo(() => {
    if (!data) return [];
    return enumerateDays(data.from_date, data.to_date);
  }, [data]);

  // Consumption grouped per (date × store × item) for the day drill-down + exports.
  // Includes store_name + city so the day-breakup can render "Store name — City" without a second fetch.
  const dayStoreItem = useMemo(() => {
    if (!data) return {};
    const m = {};
    for (const r of data.rows) {
      m[r.date] = m[r.date] || {};
      m[r.date][r.store_id] = m[r.date][r.store_id] || {
        store_name: r.store_name, city: r.city, store_code: r.store_code, items: {}
      };
      m[r.date][r.store_id].items[r.item] = r.consumed;
    }
    return m;
  }, [data]);

  // ---------- Exports (both honour active from/to/store_id) ----------
  const rangeStr = `${from}_to_${to}`;
  const exportXLSX = () => {
    if (!data) return;
    const items4 = ["BIB", "CO2", "Water Jar", "Cups"];
    const header = ["Date", "Store", ...items4];
    const rows = [];
    for (const d of days) {
      const dayEntry = dayStoreItem[d];
      // CHANGE 1 (2026-02-29 audit): skip blank/zero dates entirely — no "no data" filler rows
      if (!dayEntry) continue;
      for (const sid of Object.keys(dayEntry)) {
        const { store_name, items: perItem } = dayEntry[sid];
        rows.push([
          d, store_name,
          ...items4.map(it => (perItem[it] === undefined ? "—" : perItem[it])),
        ]);
      }
    }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      ["Coke@9 OCT · CCI Material Consumption"],
      ["Powered by BBIPL for CCI"],
      [`Range: ${from} to ${to}${storeId ? " · single store filter" : ""}`],
      ["Definition: opening + received into store − closing.  '—' = no stock entry for that day"],
      [],
      header,
      ...rows,
      [],
      ["Period totals", "", ...items4.map(it => data.totals[it] || 0)],
      [],
      ["", "", "Generated by Coke@9 OCT - powered By BBIPL"],
    ]);
    XLSX.utils.book_append_sheet(wb, ws, "Consumption");
    XLSX.writeFile(wb, `Coke9_Consumption_${rangeStr}.xlsx`);
    toast.success("Consumption XLSX downloaded");
  };
  const exportPDF = async () => {
    if (!data) return;
    const doc = await createPdfDoc();
    doc.setFontSize(14); doc.setTextColor(225, 29, 42);
    doc.text("Coke@9 OCT · CCI Material Consumption", 12, 12);
    doc.setFontSize(9); doc.setTextColor(60);
    doc.text("Powered by BBIPL for CCI", 12, 17);
    doc.setFontSize(8); doc.setTextColor(90);
    doc.text(`Range: ${from} to ${to}${storeId ? " · single store filter" : " · all stores"}`, 12, 22);
    doc.text("Store × item × day layout · quantities only (no rupee)", 12, 26);
    const items4 = ["BIB", "CO2", "Water Jar", "Cups"];
    const body = [];
    for (const d of days) {
      const dayEntry = dayStoreItem[d];
      if (!dayEntry) continue;   // skip zero/blank days
      for (const sid of Object.keys(dayEntry)) {
        const { store_name, items: perItem } = dayEntry[sid];
        body.push([d, store_name, ...items4.map(it => perItem[it] === undefined ? "—" : formatNum(perItem[it]))]);
      }
    }
    autoTable(doc, withDefaults({ startY: 30, head: [["Date", "Store", ...items4]], body }));
    drawTotalsBlock(doc,
      items4.map(it => ({ label: `${it} total`, value: formatNum(data.totals[it] || 0) })),
      { title: "Period totals" });
    finalizePdf(doc, { footer: `Generated by Coke@9 OCT - powered By BBIPL · ${new Date().toLocaleString("en-IN")}` });
    doc.save(`Coke9_Consumption_${rangeStr}.pdf`);
    toast.success("Consumption PDF downloaded");
  };

  // ---------- Render ----------
  const HeaderBar = () => (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Material consumption</div>
        <div className="text-[11px] text-gray-500 mt-0.5" data-testid="cons-period-label">{rangeLabel}{storeId ? " · one store" : " · all stores"}</div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={exportPDF} disabled={!data || loading} data-testid="cons-pdf-btn">
          <Download className="h-3.5 w-3.5 mr-1" /> PDF
        </Button>
        <Button size="sm" onClick={exportXLSX} disabled={!data || loading} className="bg-brand-red hover:bg-[#B31721]" data-testid="cons-xlsx-btn">
          <FileDown className="h-3.5 w-3.5 mr-1" /> XLSX
        </Button>
      </div>
    </div>
  );

  const Tiles = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
      {items.map(({ code, label, unit, color }) => (
        <button key={code} type="button"
          className="text-left rounded-lg border border-gray-200 p-3 hover:border-brand-red hover:bg-red-50/40 transition"
          onClick={() => setOpenItem(code)}
          data-testid={`client-consumption-${code.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</div>
          <div className={`font-display text-2xl font-black mt-1 ${color}`}>
            {data ? formatNum(data.totals[code] || 0) : "—"} <span className="text-sm font-semibold text-gray-500">{unit}</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-1" data-testid={`cons-tile-range-${code.replace(/\s+/g, "-").toLowerCase()}`}>{rangeLabel}</div>
          <div className="text-[10px] text-brand-red mt-1">Tap to view calendar →</div>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <Card className={variant === "tiles" ? "p-5 rounded-xl border-gray-200 bg-white" : "p-5 rounded-xl border-gray-200 bg-white"}
            data-testid="client-consumption-card">
        <HeaderBar />
        <Tiles />
        <div className="text-[11px] text-gray-400 mt-2">
          Formula: opening + received into store − closing. Days with no stock entry show "—", never 0.
        </div>
        {/* On-screen store × date table — same shape as the XLSX/PDF export */}
        <ConsumptionTable data={data} days={days} dayStoreItem={dayStoreItem} loading={loading} />
      </Card>

      {/* Calendar drill-down */}
      {openItem && (
        <Dialog open={!!openItem} onOpenChange={(v) => { if (!v) { setOpenItem(null); setSelectedDay(null); } }}>
          <DialogContent data-testid="consumption-calendar-dialog" className="max-w-3xl rounded-2xl" aria-describedby="cons-cal-desc">
            <DialogHeader>
              <DialogTitle>
                {selectedDay
                  ? <span className="flex items-center gap-2">
                      <button className="p-1 rounded hover:bg-gray-100" onClick={() => setSelectedDay(null)} data-testid="cons-back-to-cal">
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      {openItem} · Store-wise breakup for {selectedDay}
                    </span>
                  : <>{openItem} · Daily consumption ({rangeLabel})</>}
              </DialogTitle>
            </DialogHeader>
            <div id="cons-cal-desc" className="text-xs text-gray-500 mb-2">
              {selectedDay ? "Click any day (back arrow) to return." : "Click any day to see the store-wise breakup."}
            </div>

            {!selectedDay ? (
              // Calendar grid — one cell per day in the window
              <div className="grid grid-cols-7 gap-2" data-testid="cons-calendar-grid">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
                  <div key={d} className="text-[10px] uppercase font-bold tracking-widest text-gray-500 text-center">{d}</div>
                ))}
                {(() => {
                  if (!data) return null;
                  // Parse the range start as a LOCAL calendar date (no UTC conversion) so day-of-week aligns with the actual calendar.
                  const start = localDateFromISO(data.from_date);
                  const startWeekDay = (start.getDay() + 6) % 7;  // Mon = 0
                  const cells = [];
                  for (let i = 0; i < startWeekDay; i++) cells.push(<div key={`b${i}`} />);
                  for (const d of days) {
                    const totalForItem = data.day_totals[d]?.[openItem];
                    const hasData = totalForItem !== undefined;
                    cells.push(
                      <button key={d} type="button"
                        onClick={() => setSelectedDay(d)}
                        data-testid={`cons-day-${d}`}
                        title={d}
                        className={`rounded-lg border p-2 text-left transition ${
                          hasData
                            ? "border-red-200 bg-red-50/40 hover:border-brand-red"
                            : "border-gray-200 bg-gray-50/60 hover:border-gray-300"
                        }`}>
                        <div className="text-[10px] text-gray-500">{d.slice(-2)}</div>
                        <div className={`font-mono text-sm font-bold mt-0.5 ${hasData ? "text-brand-red" : "text-gray-400"}`}>
                          {hasData ? formatNum(totalForItem) : "—"}
                        </div>
                      </button>
                    );
                  }
                  return cells;
                })()}
              </div>
            ) : (
              // Store-wise breakup for the selected day
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cons-day-breakup-table">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left px-3 py-2">Store</th>
                      <th className="text-right px-3 py-2">BIB</th>
                      <th className="text-right px-3 py-2">CO2</th>
                      <th className="text-right px-3 py-2">Water Jar</th>
                      <th className="text-right px-3 py-2">Cups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const dayEntry = dayStoreItem[selectedDay];
                      if (!dayEntry) return (
                        <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-500">— no stock entry reported for this day —</td></tr>
                      );
                      const rows = Object.entries(dayEntry).sort(([, a], [, b]) => (a.store_name || "").localeCompare(b.store_name || ""));
                      return rows.map(([sid, { store_name, city, items: perItem }]) => (
                        <tr key={sid} className="border-t border-gray-100" data-testid={`cons-day-row-${sid}`}>
                          <td className="px-3 py-2">
                            <div className="font-semibold">{store_name || <span className="text-gray-400">Unknown store</span>}</div>
                            {city && <div className="text-[10px] text-gray-500">{city}</div>}
                          </td>
                          {["BIB", "CO2", "Water Jar", "Cups"].map(it => (
                            <td key={it} className="px-3 py-2 text-right font-mono">
                              {perItem[it] === undefined ? <span className="text-gray-400">—</span> : formatNum(perItem[it])}
                            </td>
                          ))}
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}


/**
 * Store × Date × Item table — same source as the XLSX/PDF exports. Sortable by any column.
 * Days with no stock entry render "—", never 0.
 */
function ConsumptionTable({ data, days, dayStoreItem, loading }) {
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  // Build a flat list of visible rows: one row per (store, date) that has ANY item reported that day.
  const rows = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const d of days) {
      const dayEntry = dayStoreItem[d];
      if (!dayEntry) continue;   // no data — skipped in on-screen table (rendered as absence, per spec)
      for (const sid of Object.keys(dayEntry)) {
        const s = dayEntry[sid];
        out.push({
          store_id: sid, date: d,
          store_name: s.store_name || "Unknown store",
          city: s.city || "",
          store_code: s.store_code || "",
          BIB: s.items.BIB, CO2: s.items.CO2, "Water Jar": s.items["Water Jar"], Cups: s.items.Cups,
        });
      }
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    const k = sort.key;
    return out.sort((a, b) => {
      const av = a[k] ?? -Infinity, bv = b[k] ?? -Infinity;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return ((av > bv ? 1 : av < bv ? -1 : 0)) * dir;
    });
  }, [data, days, dayStoreItem, sort]);

  const toggle = (k) => setSort(s => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: k === "store_name" ? "asc" : "desc" });
  const SortTh = ({ id, children, align = "left" }) => (
    <th className={`${align === "right" ? "text-right" : "text-left"} px-3 py-2 cursor-pointer select-none`}
        onClick={() => toggle(id)} data-testid={`cons-sort-${id}`}>
      <span className="inline-flex items-center gap-1">{children}{sort.key === id ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : null}</span>
    </th>
  );
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm" data-testid="cons-table">
        <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
          <tr>
            <SortTh id="store_name">Store</SortTh>
            <SortTh id="date">Date</SortTh>
            <SortTh id="BIB" align="right">BIB</SortTh>
            <SortTh id="CO2" align="right">CO2</SortTh>
            <SortTh id="Water Jar" align="right">Water Jar</SortTh>
            <SortTh id="Cups" align="right">Cups</SortTh>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={6} className="text-center py-6 text-gray-500">Loading…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={6} className="text-center py-6 text-gray-500">No stock entries in this range.</td></tr>
          )}
          {!loading && rows.map((r) => (
            <tr key={`${r.store_id}-${r.date}`} className="border-t border-gray-100" data-testid={`cons-table-row-${r.store_id}-${r.date}`}>
              <td className="px-3 py-2">
                <div className="font-semibold">{r.store_name}</div>
                {(r.city || r.store_code) && <div className="text-[10px] text-gray-500">{[r.store_code, r.city].filter(Boolean).join(" · ")}</div>}
              </td>
              <td className="px-3 py-2 font-mono text-xs">{r.date}</td>
              {["BIB", "CO2", "Water Jar", "Cups"].map(it => (
                <td key={it} className="px-3 py-2 text-right font-mono">
                  {r[it] === undefined ? <span className="text-gray-400">—</span> : formatNum(r[it])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
